class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.selectedIndex = -1;
  }

  connectedCallback() {
    this.render();

    // Elements
    this.button = this.querySelector("[data-modal-button]");
    this.modalContainer = this.querySelector("[data-modal-container]");
    this.backdrop = this.querySelector("[data-modal-backdrop]");
    this.modal = this.querySelector("[data-modal]");
    this.form = this.querySelector("form");

    this.onBackdropClick = this.hideModal.bind(this);

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-search-results]");

    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
    });

    // Add keyboard navigation
    this.input.addEventListener("keydown", (event) => {
      if (
        !this.predictiveSearchResults.style.display ||
        this.predictiveSearchResults.style.display === "none"
      ) {
        return;
      }

      const results = this.predictiveSearchResults.querySelectorAll(
        "[data-search-result]",
      );
      if (!results.length) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            results.length - 1,
          );
          this.updateFocus(results);
          break;
        case "ArrowUp":
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
          this.updateFocus(results);
          break;
        case "Enter":
          event.preventDefault();
          if (this.selectedIndex >= 0 && this.selectedIndex < results.length) {
            results[this.selectedIndex].click();
            this.hideModal();
          }
          break;
      }
    });

    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    // Event listeners
    this.button.addEventListener("click", this.showModal.bind(this));

    document.addEventListener("keydown", this.handleEscapeKey.bind(this));
    document.addEventListener("keydown", this.handleSlashKey.bind(this));
    // this.modal.addEventListener("click", (e) => e.stopPropagation());
  }

  render() {
    const template = document.createElement("template");
    template.innerHTML = `<slot></slot>`;
    this.shadowRoot.appendChild(template.content.cloneNode(true));
  }

  handleEscapeKey(e) {
    if (
      e.key === "Escape" &&
      !this.modalContainer.classList.contains("opacity-0")
    ) {
      this.hideModal();
    }
  }

  handleSlashKey(e) {
    // Don't trigger if user is typing in an input field or textarea
    if (
      e.key === "/" &&
      !["INPUT", "TEXTAREA"].includes(document.activeElement.tagName) &&
      !document.activeElement.isContentEditable
    ) {
      e.preventDefault();
      this.showModal();
    }
  }

  showModal() {
    this.modalContainer.classList.remove("hidden");

    this.input.focus();

    this.backdrop.classList.remove("opacity-0", "pointer-events-none");
    this.backdrop.classList.add("opacity-100");

    this.modal.classList.remove("opacity-0", "pointer-events-none", "scale-95");
    this.modal.classList.add("opacity-100", "scale-100");

    this.backdrop.addEventListener("click", this.onBackdropClick);
  }

  hideModal() {
    this.input.blur();

    this.backdrop.classList.add("opacity-0");
    this.backdrop.classList.remove("opacity-100");
    this.backdrop.classList.add("pointer-events-none");

    this.modal.classList.add("opacity-0", "pointer-events-none", "scale-95");
    this.modal.classList.remove("opacity-100", "scale-100");

    setTimeout(() => {
      this.modalContainer.classList.add("hidden");
    }, 300);
  }

  updateFocus(results) {
    results.forEach((result, index) => {
      if (index === this.selectedIndex) {
        result.setAttribute("aria-selected", "true");
        result.style.scrollMargin = "4px";
        result.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      } else {
        result.setAttribute("aria-selected", "false");
        result.style.scrollMargin = "";
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    this.selectedIndex = -1; // Reset selection when search term changes

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // need to accomodate queries like "DS-C" as sku leaning
    const isSku = /\d/.test(searchTerm) || /-/.test(searchTerm);
    const isSingleWord = !searchTerm.includes(" ");

    const fields =
      isSku && isSingleWord
        ? "variants.sku,variants.barcode,vendor"
        : "variants.sku,variants.barcode,vendor,title";

    fetch(
      `/search/suggest?q=${searchTerm}&section_id=predictive-search&resources[type]=product&resources[options][unavailable_products]=show&resources[options][fields]=${fields}&resources[options][query_scope]=variants.sku,title`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  open() {
    this.predictiveSearchResults.style.display = "block";
  }

  close() {
    this.predictiveSearchResults.style.display = "none";
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define("predictive-search", PredictiveSearch);
